import { get } from '../core/http';

const BASE_PATH = '/products';

// ============================================================================
// Types
// ============================================================================

export interface ProductImage {
  id: string;
  imageUrl: string;
  altText?: string;
  isMain: boolean;
  sortOrder: number;
}

export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  price: number;
  priceRegular?: number;
  priceSale?: number;
  comparePrice?: number;
  stock: number;
  stockQuantity?: number;
  isDefault: boolean;
  attributes: Record<string, string>;
  imageUrl?: string;
}

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  parentId?: string;
  imageUrl?: string;
  order: number;
  isActive: boolean;
  productCount?: number;
}

export interface ProductBrand {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  logoUrl?: string;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  shortDescription?: string;
  longDescription?: string;
  categoryId?: string;
  category?: ProductCategory;
  brandId?: string;
  brand?: ProductBrand;
  sku?: string;
  productType: 'simple' | 'variable';
  priceRegular?: number;
  priceSale?: number;
  stock?: number;
  isActive: boolean;
  isFeatured: boolean;
  isNew: boolean;
  isSale: boolean;
  images: ProductImage[];
  variants?: ProductVariant[];
  attributes?: Record<string, any>;
  collections?: { id: string; name: string; slug: string }[];
  weight?: number;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
    unit?: string;
  };
  metaTitle?: string;
  metaDescription?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  brandId?: string;
  inStock?: boolean;
  onSale?: boolean;
  isNew?: boolean;
  isFeatured?: boolean;
  collection?: string;
  attributes?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedProductResponse {
  data: Product[];
  meta: PaginationMeta;
  // Legacy fields for backward compatibility
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProductDetailResponse extends Product {
  relatedProducts?: Product[];
}

export interface NavigationData {
  categories: { id: string; name: string; slug: string; parentId?: string; sortOrder?: string }[];
  collections: { id: string; name: string; slug: string; parentId?: string; sortOrder?: string }[];
}

export interface FilterOptions extends NavigationData {
  attributes: Record<string, string[]>;
  minPrice: number;
  maxPrice: number;
}

// ============================================================================
// API Client
// ============================================================================

export const productsApi = {
  /**
   * Fetch paginated list of products with filters and sorting
   */
  fetchProducts: (filters?: ProductFilters): Promise<PaginatedProductResponse> => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${BASE_PATH}?${queryString}` : BASE_PATH;

    return get<PaginatedProductResponse>(url);
  },

  /**
   * Fetch single product by slug with related products
   */
  fetchProductBySlug: (slug: string): Promise<ProductDetailResponse> => {
    return get<ProductDetailResponse>(`${BASE_PATH}/by-slug/${slug}`);
  },

  /**
   * Fetch related products for a given product ID
   */
  fetchRelatedProducts: (productId: string, limit: number = 4): Promise<Product[]> => {
    return get<Product[]>(`${BASE_PATH}/${productId}/related?limit=${limit}`);
  },

  /**
   * Fetch search suggestions based on partial query
   */
  fetchSearchSuggestions: (query: string, limit: number = 5): Promise<string[]> => {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
    });

    return get<string[]>(`${BASE_PATH}/search/suggestions?${params.toString()}`);
  },

  /**
   * Fetch featured products
   */
  fetchFeaturedProducts: (limit: number = 8): Promise<Product[]> => {
    return get<Product[]>(`${BASE_PATH}/featured?limit=${limit}`);
  },

  /**
   * Fetch single product by ID
   */
  fetchProductById: (id: string): Promise<Product> => {
    return get<Product>(`${BASE_PATH}/${id}`);
  },

  /**
   * Fetch categories and collections for global navigation (MegaMenu)
   */
  fetchNavigationData: async (): Promise<NavigationData> => {
    try {
      const response = await get<NavigationData>(`${BASE_PATH}/navigation`);
      return response;
    } catch (error) {
      console.error('Error in fetchNavigationData:', error);
      return {
        categories: [],
        collections: [],
      };
    }
  },

  /**
   * Fetch dynamic filter options (categories, collections, attributes, prices)
   * Scans active products and variants to find available filter values
   */
  fetchFilterOptions: async (): Promise<FilterOptions> => {
    try {
      const response = await get<any>(`${BASE_PATH}/filters`);

      const attrMap: Record<string, string[]> = {};
      if (response && response.attributes) {
        response.attributes.forEach((attr: any) => {
          if (attr.name && attr.values) {
            const sortedValues = Array.from(attr.values).sort((a: any, b: any) => {
              const lowKey = attr.name.toLowerCase();
              if (lowKey === 'talla' || lowKey === 'size') {
                const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];
                const stringA = String(a).toUpperCase();
                const stringB = String(b).toUpperCase();
                const indexA = sizeOrder.indexOf(stringA);
                const indexB = sizeOrder.indexOf(stringB);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
              }
              return String(a).localeCompare(String(b));
            });
            attrMap[attr.name] = sortedValues as string[];
          }
        });
      }

      return {
        ...response,
        attributes: attrMap,
        minPrice: response.minPrice || 0,
        maxPrice: response.maxPrice || 500000
      }
    } catch (error: any) {
      console.error('Error fetching filter options from backend:', error);
      return {
        categories: [],
        collections: [],
        attributes: {},
        minPrice: 0,
        maxPrice: 500000,
      };
    }
  },
};
